/**
 * Danışan dosyasının davranış takibi.
 *
 * Ölçüm cihazda ne yapabildiğini gösteriyor, gözlem formu genel bir
 * resim veriyor. Bu ise TEK BİR davranışı haftalar boyunca sayıyor;
 * programın işe yarayıp yaramadığı en net burada görünüyor.
 *
 * ÜÇ KAYNAK AYRI ÇİZİLİYOR, ortalamaları alınmıyor: ev ve sınıf
 * farklı ortamlar ve ikisinin ayrışması asıl bulgu. Okuma cümleleri
 * sunucudan geliyor — eşik ve yön kuralı tek bir yerde durması için.
 *
 * NORM YOK: kesme noktası, yüzdelik ya da "normal aralık" yok.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  DAVRANIS_YONU_ADI,
  davranisApi,
  haftaBasi,
  type DavranisHedefi,
  type DavranisYonu,
} from '../api'

const KAYNAK_ADI: Record<string, string> = {
  parent: 'Veli',
  teacher: 'Öğretmen',
  clinician: 'Uygulayıcı',
  self: 'Danışanın kendisi',
}

/** Kaynak başına ayrı renk: grafikte hangi çizgi kim olduğu belli olsun */
const KAYNAK_RENGI: Record<string, string> = {
  parent: 'var(--accent)',
  teacher: 'var(--warn)',
  clinician: 'var(--ok)',
  self: 'var(--muted)',
}

const gun = (s: string) =>
  new Date(s + 'T00:00:00Z').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })

export function DosyaDavranis({
  danisanId,
  yazabilir,
}: {
  danisanId: string
  yazabilir: boolean
}) {
  const [hedefler, setHedefler] = useState<DavranisHedefi[] | null>(null)
  const [kapaliGoster, setKapaliGoster] = useState(false)
  const [ekleAcik, setEkleAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await davranisApi.liste(danisanId, kapaliGoster)
      setHedefler(r.hedefler)
    } catch {
      setHata('Davranış hedefleri alınamadı.')
      setHedefler([])
    }
  }, [danisanId, kapaliGoster])

  useEffect(() => {
    void yukle()
  }, [yukle])

  return (
    <div className="kart">
      <h2 style={{ margin: '0 0 4px' }}>Davranış takibi</h2>
      <p className="alt" style={{ marginBottom: 12 }}>
        Tek bir davranışı haftalar boyunca sayıyoruz. Veli portaldan, öğretmen kendi
        adresinden, uygulayıcı buradan girebiliyor; sayılar <strong>ayrı</strong> tutuluyor —
        ev ve sınıf aynı şeyi saymıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      {hedefler === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : hedefler.length === 0 ? (
        <p className="alt">
          {kapaliGoster ? 'Kapatılmış hedef de yok.' : 'Takip edilen davranış yok.'}
        </p>
      ) : (
        hedefler.map((h) => (
          <Hedef key={h.id} h={h} yazabilir={yazabilir} onDegisti={() => void yukle()} />
        ))
      )}

      <div className="dugmeler">
        {yazabilir && (
          <button onClick={() => setEkleAcik((x) => !x)}>
            {ekleAcik ? 'Vazgeç' : 'Davranış ekle'}
          </button>
        )}
        <button onClick={() => setKapaliGoster((x) => !x)}>
          {kapaliGoster ? 'Yalnızca açık olanlar' : 'Kapatılmışları da göster'}
        </button>
      </div>

      {ekleAcik && (
        <HedefEkle
          danisanId={danisanId}
          onEklendi={() => {
            setEkleAcik(false)
            void yukle()
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function Hedef({
  h,
  yazabilir,
  onDegisti,
}: {
  h: DavranisHedefi
  yazabilir: boolean
  onDegisti: () => void
}) {
  const [acik, setAcik] = useState(false)
  const kaynaklar = [...new Set(h.kayitlar.map((k) => k.kaynak))]

  return (
    <div style={{ padding: '12px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={h.aktif ? undefined : { opacity: 0.6 }}>{h.metin}</strong>
        <span className="alt" style={{ margin: 0 }}>
          {h.birim} · {DAVRANIS_YONU_ADI[h.yon]}
          {h.baslangic !== null ? ` · başlangıç ${h.baslangic}` : ''}
          {h.hedef !== null ? ` · hedef ${h.hedef}` : ''}
        </span>
        {!h.aktif && <span className="rozet">Kapatıldı</span>}
      </div>

      <Grafik h={h} />

      {kaynaklar.length === 0 ? (
        <p className="alt" style={{ marginBottom: 0 }}>
          Henüz kayıt yok. Veli portalda bu hedefi görüyor ve haftalık sayıyı oradan
          girebiliyor.
        </p>
      ) : (
        kaynaklar.map((k) => {
          const o = h.okumalar[k]
          return (
            <p className="alt" key={k} style={{ margin: '4px 0 0' }}>
              <span style={{ color: KAYNAK_RENGI[k] ?? 'inherit', fontWeight: 600 }}>
                {KAYNAK_ADI[k] ?? k}
              </span>
              {o ? ` — ${o.cumle}` : ''}
              {o?.ilerleme != null ? ` Hedefe %${Math.round(o.ilerleme * 100)}.` : ''}
            </p>
          )
        })
      )}

      <div className="dugmeler">
        {yazabilir && h.aktif && (
          <button onClick={() => setAcik((x) => !x)}>
            {acik ? 'Kapat' : 'Haftalık sayı gir'}
          </button>
        )}
        {yazabilir &&
          (h.aktif ? (
            <button
              onClick={async () => {
                await davranisApi.kapat(h.id)
                onDegisti()
              }}
            >
              Takibi kapat
            </button>
          ) : (
            <button
              onClick={async () => {
                await davranisApi.ac(h.id)
                onDegisti()
              }}
            >
              Yeniden aç
            </button>
          ))}
      </div>

      {acik && <KayitGir h={h} onKaydedildi={onDegisti} />}
    </div>
  )
}

/* ------------------------------------------------------------------ */

/**
 * Küçük çizgi grafiği.
 *
 * Kütüphane yok: tek bir SVG. Eksen sıfırdan BAŞLAMIYOR, veri
 * aralığına oturuyor — haftalık 18 ve 20 arasındaki fark, sıfırdan
 * başlayan bir eksende görünmez oluyor ve asıl bakılan şey o fark.
 * Hedef varsa yatay çizgi olarak duruyor.
 */
function Grafik({ h }: { h: DavranisHedefi }) {
  if (h.kayitlar.length === 0) return null

  const G = 560
  const Y = 130
  const PAD = { sol: 34, sag: 10, ust: 10, alt: 22 }

  const haftalar = [...new Set(h.kayitlar.map((k) => k.hafta))].sort()
  const degerler = h.kayitlar.map((k) => k.deger)
  if (h.hedef !== null) degerler.push(h.hedef)
  if (h.baslangic !== null) degerler.push(h.baslangic)

  let enAz = Math.min(...degerler)
  let enCok = Math.max(...degerler)
  if (enAz === enCok) {
    enAz = Math.max(0, enAz - 1)
    enCok = enCok + 1
  }
  const bosluk = (enCok - enAz) * 0.1
  enAz = Math.max(0, enAz - bosluk)
  enCok = enCok + bosluk

  const x = (hafta: string) =>
    PAD.sol +
    (haftalar.length === 1
      ? (G - PAD.sol - PAD.sag) / 2
      : (haftalar.indexOf(hafta) / (haftalar.length - 1)) * (G - PAD.sol - PAD.sag))
  const y = (d: number) =>
    PAD.ust + (1 - (d - enAz) / (enCok - enAz)) * (Y - PAD.ust - PAD.alt)

  const kaynaklar = [...new Set(h.kayitlar.map((k) => k.kaynak))]

  return (
    <svg
      viewBox={`0 0 ${G} ${Y}`}
      style={{ width: '100%', maxWidth: G, height: 'auto', display: 'block', margin: '8px 0' }}
      role="img"
      aria-label={`${h.metin} haftalık kayıtlar`}
    >
      {/* Hedef çizgisi */}
      {h.hedef !== null && (
        <>
          <line
            x1={PAD.sol}
            x2={G - PAD.sag}
            y1={y(h.hedef)}
            y2={y(h.hedef)}
            stroke="var(--ok)"
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          <text x={2} y={y(h.hedef) + 3} fontSize={9} fill="var(--ok)">
            {h.hedef}
          </text>
        </>
      )}
      {/* Başlangıç çizgisi */}
      {h.baslangic !== null && (
        <line
          x1={PAD.sol}
          x2={G - PAD.sag}
          y1={y(h.baslangic)}
          y2={y(h.baslangic)}
          stroke="var(--line)"
          strokeWidth={1}
        />
      )}

      {/* Her kaynak kendi çizgisi */}
      {kaynaklar.map((k) => {
        const noktalar = h.kayitlar
          .filter((r) => r.kaynak === k)
          .sort((a, b) => a.hafta.localeCompare(b.hafta))
        const renk = KAYNAK_RENGI[k] ?? 'var(--ink)'
        return (
          <g key={k}>
            {noktalar.length > 1 && (
              <polyline
                fill="none"
                stroke={renk}
                strokeWidth={1.6}
                points={noktalar.map((r) => `${x(r.hafta)},${y(r.deger)}`).join(' ')}
              />
            )}
            {noktalar.map((r) => (
              <circle key={r.id} cx={x(r.hafta)} cy={y(r.deger)} r={2.6} fill={renk}>
                <title>{`${KAYNAK_ADI[k] ?? k} · ${gun(r.hafta)} · ${r.deger} ${h.birim}`}</title>
              </circle>
            ))}
          </g>
        )
      })}

      {/* Hafta etiketleri: ilk, orta, son — hepsini yazmak okunmuyor */}
      {[0, Math.floor((haftalar.length - 1) / 2), haftalar.length - 1]
        .filter((i, idx, a) => i >= 0 && a.indexOf(i) === idx)
        .map((i) => (
          <text
            key={i}
            x={x(haftalar[i]!)}
            y={Y - 6}
            fontSize={9}
            fill="var(--muted)"
            textAnchor={i === 0 ? 'start' : i === haftalar.length - 1 ? 'end' : 'middle'}
          >
            {gun(haftalar[i]!)}
          </text>
        ))}
    </svg>
  )
}

/* ------------------------------------------------------------------ */

function KayitGir({ h, onKaydedildi }: { h: DavranisHedefi; onKaydedildi: () => void }) {
  const [hafta, setHafta] = useState(haftaBasi())
  const [deger, setDeger] = useState('')
  const [not, setNot] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const kaydet = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await davranisApi.kayit(h.id, {
        hafta,
        deger: Number(deger),
        kaynak: 'clinician',
        not: not.trim() || undefined,
      })
      setDeger('')
      setNot('')
      onKaydedildi()
    } catch (x) {
      setHata(
        x instanceof ApiHatasi && x.kod === 'gelecek_hafta'
          ? 'Gelecek haftaya kayıt girilemiyor.'
          : x instanceof ApiHatasi && x.kod === 'hedef_kapali'
            ? 'Bu hedef kapatılmış.'
            : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  /*
   * BOŞ TARİH ÇİZİM SIRASINDA ÇÖKÜYORDU.
   *
   * `<input type="date">` temizlenebiliyor ve değeri `''` oluyor.
   * `new Date('T00:00:00')` geçersiz tarih; `haftaBasi` onun üstünde
   * `toISOString()` çağırıp RangeError atıyordu. Hata ÇİZİM sırasında
   * olduğu için React ağacı düşüyor ve uygulamada hiç
   * ErrorBoundary olmadığı için danışan dosyası komple BEYAZ EKRANA
   * dönüyordu. Kullanıcının yaptığı tek şey hafta alanını silmekti.
   */
  const haftaIso = hafta ? haftaBasi(new Date(`${hafta}T00:00:00`)) : null
  const mevcut = haftaIso
    ? h.kayitlar.find((k) => k.kaynak === 'clinician' && k.hafta === haftaIso)
    : undefined

  return (
    <form onSubmit={kaydet} style={{ marginTop: 8 }}>
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        <label>
          Hafta
          <input
            type="date"
            value={hafta}
            max={haftaBasi()}
            onChange={(e) => setHafta(e.target.value)}
            required
          />
        </label>
        <label>
          Değer ({h.birim})
          <input
            type="number"
            min={0}
            step="any"
            value={deger}
            onChange={(e) => setDeger(e.target.value)}
            required
          />
        </label>
        <label>
          Not
          <input value={not} onChange={(e) => setNot(e.target.value)} maxLength={500} />
        </label>
      </div>
      <p className="alt" style={{ marginTop: -6 }}>
        Hangi günü seçerseniz o haftanın pazartesisine yazılıyor.
        {mevcut ? ` Bu hafta için kayıtlı değer ${mevcut.deger}; üstüne yazılacak.` : ''}
      </p>
      <button className="birincil" type="submit" disabled={bekliyor || deger === ''}>
        {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
      </button>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function HedefEkle({
  danisanId,
  onEklendi,
}: {
  danisanId: string
  onEklendi: () => void
}) {
  const [metin, setMetin] = useState('')
  const [birim, setBirim] = useState('')
  const [yon, setYon] = useState<DavranisYonu>('lower')
  const [baslangic, setBaslangic] = useState('')
  const [hedef, setHedef] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const kaydet = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      await davranisApi.ekle(danisanId, {
        metin: metin.trim(),
        birim: birim.trim(),
        yon,
        baslangic: baslangic === '' ? null : Number(baslangic),
        hedef: hedef === '' ? null : Number(hedef),
      })
      onEklendi()
    } catch (x) {
      setHata(
        x instanceof ApiHatasi && x.kod === 'hedef_yonle_tutarsiz'
          ? yon === 'lower'
            ? '“Azalması iyi” seçtiniz; hedef başlangıçtan küçük olmalı.'
            : '“Artması iyi” seçtiniz; hedef başlangıçtan büyük olmalı.'
          : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form
      onSubmit={kaydet}
      style={{ borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
    >
      {hata && <div className="uyari">{hata}</div>}
      <label>
        Takip edilen davranış
        <input
          value={metin}
          onChange={(e) => setMetin(e.target.value)}
          required
          minLength={3}
          maxLength={500}
          placeholder="Ödevine kendi başına başlaması"
        />
      </label>
      <div className="satir">
        <label>
          Birim
          <input
            value={birim}
            onChange={(e) => setBirim(e.target.value)}
            required
            maxLength={80}
            placeholder="kez / hafta"
          />
        </label>
        <label>
          Hangi yön iyileşme
          <select value={yon} onChange={(e) => setYon(e.target.value as DavranisYonu)}>
            <option value="lower">Azalması iyi</option>
            <option value="higher">Artması iyi</option>
          </select>
        </label>
        <label>
          Başlangıç
          <input
            type="number"
            min={0}
            step="any"
            value={baslangic}
            onChange={(e) => setBaslangic(e.target.value)}
          />
        </label>
        <label>
          Hedef
          <input
            type="number"
            min={0}
            step="any"
            value={hedef}
            onChange={(e) => setHedef(e.target.value)}
          />
        </label>
      </div>
      <p className="alt" style={{ marginTop: -6 }}>
        Yön ayrı soruluyor çünkü hedeften çıkarılamıyor: hedefi boş bırakabilirsiniz ve o
        zaman hangi yönün iyileşme olduğu belirsiz kalırdı. Başlangıç ve hedef birlikte
        girilirse yönle tutarlı olmaları gerekiyor.
      </p>
      <button className="birincil" type="submit" disabled={bekliyor}>
        {bekliyor ? 'Kaydediliyor…' : 'Ekle'}
      </button>
    </form>
  )
}
